import type { TConnector, TConnectorField, TCredential, TCredentialUsage } from './api';

export type TCredentialApp =
  'anthropic' | 'openai' | 'openai_compatible' | 'slack' | 'telegram' | 'smtp' | 'http';

export interface ICredentialFieldSpec {
  path: string;
  label: string;
  hint?: string;
}

export interface ICredentialApp {
  app: TCredentialApp;
  choice: string;
  title: string;
  subtitle?: string;
  connectorKey: string;
  /** Fields the person does not type: the AI provider. */
  preset: Record<string, string>;
  fields: readonly ICredentialFieldSpec[];
  secretPath: string;
  secretWord: string;
  placeholderName: string;
}

// Board 13's words and order; the field kinds, options and rules come from the catalog.
export const CREDENTIAL_APPS: readonly ICredentialApp[] = [
  {
    app: 'anthropic',
    choice: 'Anthropic',
    title: 'Anthropic',
    connectorKey: 'ai',
    preset: { provider: 'anthropic' },
    fields: [{ path: 'apiKey', label: 'API key' }],
    secretPath: 'apiKey',
    secretWord: 'API key',
    placeholderName: 'Anthropic · prod',
  },
  {
    app: 'openai',
    choice: 'OpenAI',
    title: 'OpenAI',
    connectorKey: 'ai',
    preset: { provider: 'openai' },
    fields: [{ path: 'apiKey', label: 'API key' }],
    secretPath: 'apiKey',
    secretWord: 'API key',
    placeholderName: 'OpenAI · prod',
  },
  {
    app: 'openai_compatible',
    choice: 'OpenAI-compatible',
    title: 'OpenAI-compatible',
    connectorKey: 'ai',
    preset: { provider: 'openai_compatible' },
    fields: [
      { path: 'baseUrl', label: 'Base URL' },
      { path: 'apiKey', label: 'API key' },
    ],
    secretPath: 'apiKey',
    secretWord: 'API key',
    placeholderName: 'Together · llama',
  },
  {
    app: 'slack',
    choice: 'Slack bot',
    title: 'Slack bot',
    subtitle: 'From api.slack.com › your app › OAuth & Permissions.',
    connectorKey: 'slack',
    preset: {},
    fields: [
      {
        path: 'botToken',
        label: 'Bot token',
        hint: 'needs chat:write, and channels:read to list channels',
      },
    ],
    secretPath: 'botToken',
    secretWord: 'bot token',
    placeholderName: 'LeadGet bot',
  },
  {
    app: 'telegram',
    choice: 'Telegram bot',
    title: 'Telegram bot',
    subtitle: 'From @BotFather.',
    connectorKey: 'telegram',
    preset: {},
    fields: [{ path: 'botToken', label: 'Bot token' }],
    secretPath: 'botToken',
    secretWord: 'bot token',
    placeholderName: 'oncall bot',
  },
  {
    app: 'smtp',
    choice: 'SMTP',
    title: 'SMTP',
    connectorKey: 'email',
    preset: {},
    fields: [
      { path: 'host', label: 'Host' },
      { path: 'port', label: 'Port' },
      { path: 'security', label: 'Security' },
      { path: 'username', label: 'User' },
      { path: 'password', label: 'Password' },
      { path: 'from', label: 'From' },
    ],
    secretPath: 'password',
    secretWord: 'password',
    placeholderName: 'SMTP · Postmark',
  },
  {
    app: 'http',
    choice: 'HTTP',
    title: 'HTTP',
    subtitle: 'For HTTP request steps and destinations.',
    connectorKey: 'http',
    preset: {},
    fields: [
      { path: 'scheme', label: 'Sends' },
      { path: 'username', label: 'User' },
      { path: 'headerName', label: 'Header name' },
      { path: 'token', label: 'Token' },
    ],
    secretPath: 'token',
    secretWord: 'token',
    placeholderName: 'CRM API',
  },
];

/** Which of the seven a stored credential is: the connector, then its provider or scheme. */
export const appOf = (credential: TCredential): ICredentialApp | null => {
  if (credential.connectorKey === 'ai') {
    return CREDENTIAL_APPS.find((app) => app.preset.provider === credential.variant) ?? null;
  }
  return CREDENTIAL_APPS.find((app) => app.connectorKey === credential.connectorKey) ?? null;
};

const SCHEME_WORD: Record<string, string> = { bearer: 'bearer', basic: 'basic', header: 'header' };

/** The "Kind" column: "Anthropic", "Slack · bot token", "HTTP · bearer". */
export const kindLabel = (credential: TCredential, connectors: readonly TConnector[]): string => {
  const app = appOf(credential);
  if (app?.app === 'http') {
    const scheme = credential.variant ? SCHEME_WORD[credential.variant] : undefined;
    return scheme ? `HTTP · ${scheme}` : 'HTTP';
  }
  if (app?.app === 'slack') return 'Slack · bot token';
  if (app) return app.choice.replace(/ bot$/, '');
  return (
    connectors.find((connector) => connector.key === credential.connectorKey)?.name ??
    credential.connectorKey
  );
};

/** The server's hint without the provider or scheme the Kind column already names. */
export const secretHint = (credential: TCredential): string => {
  const hint = credential.hint ?? '—';
  const lead = credential.variant ? `${credential.variant} · ` : null;
  return lead && hint.startsWith(lead) ? hint.slice(lead.length) : hint;
};

/** HTTP basic asks for a password where the others ask for a token. */
export const secretWordOf = (app: ICredentialApp, variant: string | null): string =>
  app.app === 'http' && variant === 'basic' ? 'password' : app.secretWord;

export const replaceTitle = (app: ICredentialApp, variant: string | null): string =>
  `Replace the ${app.app === 'smtp' ? 'SMTP' : app.title} ${secretWordOf(app, variant)}`;

export const catalogField = (
  connectors: readonly TConnector[],
  connectorKey: string,
  path: string,
): TConnectorField | undefined =>
  connectors
    .find((connector) => connector.key === connectorKey && connector.latest)
    ?.credential?.fields.find((field) => field.path === path);

const plural = (count: number, one: string) =>
  `${count.toLocaleString('en-US')} ${one}${count === 1 ? '' : 's'}`;

/** "2 steps · Stripe production", "3 steps · 2 flows", "nothing". */
export const usageLine = (usage: TCredentialUsage): string => {
  if (usage.steps === 0) return 'nothing';
  const where =
    usage.flows === 1
      ? (usage.uses[0]?.flowName ?? plural(1, 'flow'))
      : plural(usage.flows, 'flow');
  return `${plural(usage.steps, 'step')} · ${where}`;
};

export const stepCount = (count: number): string => plural(count, 'step');

export interface ICredentialStep {
  flowId: string;
  flowName: string;
  nodeKey: string;
  nodeName: string;
  /** Where it is named: "v7", "draft" or "v7 and the draft". */
  where: string;
}

const whereWords = (versions: readonly (number | null)[]): string => {
  const published = versions.filter((version): version is number => version !== null);
  const draft = versions.includes(null);
  const newest = published.length > 0 ? `v${Math.max(...published)}` : null;
  if (newest && draft) return `${newest} and the draft`;
  return newest ?? 'draft';
};

interface IStepAcc {
  flowId: string;
  flowName: string;
  nodeKey: string;
  nodeName: string;
  versions: (number | null)[];
}

const collect = (
  rows: readonly {
    flowId: string;
    flowName: string;
    version: number | null;
    nodeKey: string;
    nodeName: string;
  }[],
): ICredentialStep[] => {
  const steps = new Map<string, IStepAcc>();
  for (const row of rows) {
    const key = `${row.flowId}:${row.nodeKey}`;
    const found = steps.get(key);
    if (found) {
      found.versions.push(row.version);
      // The draft's name is the one the canvas shows now.
      if (row.version === null) found.nodeName = row.nodeName;
    } else {
      steps.set(key, { ...row, versions: [row.version] });
    }
  }
  return [...steps.values()].map(({ versions, ...step }) => ({
    ...step,
    where: whereWords(versions),
  }));
};

/** One row per step, whichever versions and draft name it. */
export const usageSteps = (usage: TCredentialUsage): ICredentialStep[] =>
  collect(
    usage.uses.flatMap((use) =>
      use.nodeKeys.map((nodeKey) => ({
        flowId: use.flowId,
        flowName: use.flowName,
        version: use.version,
        nodeKey,
        nodeName: use.nodes.find((node) => node.key === nodeKey)?.name ?? nodeKey,
      })),
    ),
  );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// 409 `credentials.in_use` names the steps in `details.nodes[]` (docs/flows-api.md).
export const inUseSteps = (details: unknown): ICredentialStep[] => {
  if (!isRecord(details) || !Array.isArray(details.nodes)) return [];
  return collect(
    details.nodes.flatMap((node) => {
      if (!isRecord(node)) return [];
      const { flowId, flowName, version, nodeKey, nodeName } = node;
      if (typeof flowId !== 'string' || typeof nodeKey !== 'string') return [];
      return [
        {
          flowId,
          flowName: typeof flowName === 'string' ? flowName : flowId,
          version: typeof version === 'number' ? version : null,
          nodeKey,
          nodeName: typeof nodeName === 'string' ? nodeName : nodeKey,
        },
      ];
    }),
  );
};
